import React, { useState } from 'react';
import { X, Tag, Search } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EquipmentTagging = ({ equipment, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(equipment || []);

  const availableEquipment = [
    { id: 'COMP-01', name: 'Main Compressor', type: 'Rotating Equipment', location: 'Module A' },
    { id: 'PSV-101', name: 'Pressure Safety Valve', type: 'Safety Device', location: 'Module A' },
    { id: 'TK-201', name: 'Storage Tank', type: 'Static Equipment', location: 'Tank Farm' },
    { id: 'P-101', name: 'Feed Pump', type: 'Rotating Equipment', location: 'Pump House' },
    { id: 'HX-301', name: 'Heat Exchanger', type: 'Static Equipment', location: 'Module B' },
    { id: 'V-401', name: 'Separator Vessel', type: 'Static Equipment', location: 'Module C' },
    { id: 'HTR-01', name: 'Process Heater', type: 'Fired Equipment', location: 'Module B' },
    { id: 'FT-201', name: 'Flow Transmitter', type: 'Instrumentation', location: 'Module A' },
  ];

  const filteredEquipment = availableEquipment?.filter(equip =>
    searchQuery === '' ||
    equip?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    equip?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    equip?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const toggleEquipment = (equipId) => {
    setSelectedEquipment(prev =>
      prev?.includes(equipId)
        ? prev?.filter(id => id !== equipId)
        : [...prev, equipId]
    );
  };

  const getEquipmentTypeColor = (type) => {
    const colors = {
      'Rotating Equipment': 'bg-blue-100 text-blue-800',
      'Static Equipment': 'bg-green-100 text-green-800',
      'Safety Device': 'bg-red-100 text-red-800',
      'Instrumentation': 'bg-purple-100 text-purple-800',
      'Fired Equipment': 'bg-orange-100 text-orange-800',
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Tag className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Equipment Tagging</h2>
              <p className="text-sm text-gray-600 mt-1">Link hazards to specific equipment</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <Input
            type="text"
            placeholder="Search equipment by ID, name, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            icon={<Search className="w-5 h-5 text-gray-400" />}
          />
        </div>

        {/* Selected Equipment Summary */}
        {selectedEquipment?.length > 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedEquipment?.length} equipment item(s) selected
              </span>
              <button
                onClick={() => setSelectedEquipment([])}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Equipment List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredEquipment?.map((equip) => {
              const isSelected = selectedEquipment?.includes(equip?.id);
              
              return (
                <div
                  key={equip?.id}
                  onClick={() => toggleEquipment(equip?.id)}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-semibold text-gray-900">{equip?.id}</span>
                      </div>
                      <div className="text-sm text-gray-700 mb-2">{equip?.name}</div>
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getEquipmentTypeColor(equip?.type)}`}>
                          {equip?.type}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {equip?.location}
                        </span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleEquipment(equip?.id)}
                      className="w-5 h-5 text-blue-600 rounded"
                      onClick={(e) => e?.stopPropagation()}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            Select equipment to link with the hazard
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={selectedEquipment?.length === 0}
            >
              Link Equipment ({selectedEquipment?.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentTagging;